/**
 * Hash router (P1-T11). No dependencies — the route table is tiny.
 *
 * Routes:
 *   #/home, #/            → Home tab
 *   #/explore              → Explore tab
 *   #/create               → Create tab (all tools)
 *   #/collection           → Collection tab
 *   #/welcome              → first-launch welcome (public)
 *   #/login                → magic-link sign-in (public)
 *   #/auth/verify?token=… → magic-link callback (public)
 *   #/design               → design-system preview (public, dev)
 *   #/template/:slug       → template detail (P1-T13)
 *   #/editor/:tool         → generic editor (P1-T14)
 *   #/result/:jobId        → generation result (P1-T15)
 *   #/paywall              → credit packs (P1-T17)
 *   #/checkout/:id         → order review for a pack/subscription id
 *   #/profile              → Profile tab
 *   #/settings[/:sub]      → Settings and sub-pages (from Profile)
 */

export type RouteName =
  | 'home'
  | 'explore'
  | 'create'
  | 'collection'
  | 'welcome'
  | 'login'
  | 'verify'
  | 'design'
  | 'template'
  | 'category'
  | 'editor'
  | 'result'
  | 'paywall'
  | 'checkout'
  | 'profile'
  | 'settings'
  | 'notfound';

export interface Route {
  name: RouteName;
  params: Record<string, string>;
  query: URLSearchParams;
}

export function parseHash(hash: string): Route {
  const stripped = hash.startsWith('#') ? hash.slice(1) : hash;
  const [pathPart = '', queryPart = ''] = stripped.split('?');
  const query = new URLSearchParams(queryPart);
  const segs = pathPart.split('/').filter((s) => s.length > 0);
  const params: Record<string, string> = {};

  let name: RouteName = 'notfound';
  if (segs.length === 0 || (segs.length === 1 && segs[0] === 'home')) {
    name = 'home';
  } else if (segs.length === 1 && segs[0] === 'explore') {
    name = 'explore';
  } else if (segs.length === 1 && segs[0] === 'create') {
    name = 'create';
  } else if (segs.length === 1 && segs[0] === 'collection') {
    name = 'collection';
  } else if (segs.length === 1 && segs[0] === 'welcome') {
    name = 'welcome';
  } else if (segs.length === 1 && segs[0] === 'login') {
    name = 'login';
  } else if (segs.length === 1 && segs[0] === 'design') {
    name = 'design';
  } else if (segs.length === 2 && segs[0] === 'auth' && segs[1] === 'verify') {
    name = 'verify';
  } else if (segs.length === 2 && segs[0] === 'category') {
    name = 'category';
    params['slug'] = decodeURIComponent(segs[1] ?? '');
  } else if (segs.length === 2 && segs[0] === 'template') {
    name = 'template';
    params['slug'] = decodeURIComponent(segs[1] ?? '');
  } else if (segs.length === 2 && segs[0] === 'editor') {
    name = 'editor';
    params['tool'] = decodeURIComponent(segs[1] ?? '');
  } else if (segs.length === 2 && segs[0] === 'result') {
    name = 'result';
    params['jobId'] = decodeURIComponent(segs[1] ?? '');
  } else if (segs.length === 1 && segs[0] === 'paywall') {
    name = 'paywall';
  } else if (segs.length === 2 && segs[0] === 'checkout') {
    name = 'checkout';
    params['id'] = decodeURIComponent(segs[1] ?? '');
  } else if (segs.length === 1 && segs[0] === 'profile') {
    name = 'profile';
  } else if (segs.length >= 1 && segs[0] === 'settings') {
    name = 'settings';
    params['sub'] = decodeURIComponent(segs[1] ?? '');
  }

  return { name, params, query };
}

/** Navigate to a hash path like `/home` or `/template/some-slug`. */
export function navigate(path: string): void {
  const target = `#${path.startsWith('/') ? path : `/${path}`}`;
  if (window.location.hash === target) {
    // Same hash: force a re-render.
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  } else {
    window.location.hash = target;
  }
}
